"use client";

import { Button, Input, Label, Loader, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, ShoppingCart } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { UpdatePurchaseOrderDTO } from "@/modules/merchant/domain/contracts";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { PurchaseOrderStatusBadge } from "@/modules/merchant/presentation/ui/PurchaseOrderStatusBadge";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import {
  type PoDraftLine,
  ShopPurchaseOrderDraftLinesEditor,
} from "@/modules/merchant/presentation/ui/ShopPurchaseOrderDraftLinesEditor";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatShopMoney, SHOP_CURRENCIES } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  useCancelPurchaseOrderMutation,
  usePurchaseOrderDetailQuery,
  usePurchaseOrderReceiptsQuery,
  useSubmitPurchaseOrderMutation,
  useSuppliersListQuery,
  useUpdatePurchaseOrderMutation,
} from "../api/queries";

const LIST_ROUTE = "/app/shops/$shopId/purchase-orders" as const;
const PO_RECEIVE_ROUTE =
  "/app/shops/$shopId/purchase-orders/$purchaseOrderId/receive" as const;

export function ShopPurchaseOrderDetailPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const purchaseOrderId = params?.purchaseOrderId as string | undefined;
  const nav = useNavigate();

  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canAdjust = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.adjust,
    verifiedLevel,
  );
  const canReceive = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.receive,
    verifiedLevel,
  );

  const detail = usePurchaseOrderDetailQuery(shopId, purchaseOrderId);
  const d = detail.data;
  const suppliersQuery = useSuppliersListQuery(shopId, {
    limit: 100,
    offset: 0,
    activeOnly: true,
  });
  const receiptsQuery = usePurchaseOrderReceiptsQuery(shopId, purchaseOrderId, {
    enabled: !!shopId && !!purchaseOrderId && !!d && d.status !== "draft",
  });
  const updatePo = useUpdatePurchaseOrderMutation(shopId);
  const submitPo = useSubmitPurchaseOrderMutation(shopId);
  const cancelPo = useCancelPurchaseOrderMutation(shopId);

  const isDraft = d?.status === "draft";

  const firstReceivableSkuId = useMemo(() => {
    if (!d?.lines?.length) return undefined;
    const row = d.lines.find((l) => l.qtyOrdered > l.qtyReceived);
    return row?.shopProductSkuId;
  }, [d?.lines]);

  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState("");
  const [supplierRef, setSupplierRef] = useState("");
  const [note, setNote] = useState("");
  const [currency, setCurrency] = useState<string>("LAK");
  const [draftLines, setDraftLines] = useState<PoDraftLine[]>([]);

  useEffect(() => {
    if (!d) return;
    setSupplierId(d.supplierId ?? null);
    setSupplierName(d.supplierName ?? "");
    setSupplierRef(d.supplierRef ?? "");
    setNote(d.note ?? "");
    setCurrency(d.currency);
    if (d.status === "draft") {
      setDraftLines(
        d.lines.map((l) => ({
          clientKey: l.id,
          shopProductSkuId: l.shopProductSkuId,
          qtyOrdered: l.qtyOrdered,
          unitCost: l.unitCost,
          lineNote: l.lineNote ?? undefined,
        })),
      );
    }
  }, [d]);

  const currencyOptions = useMemo(
    () => SHOP_CURRENCIES.map((c) => ({ value: c, label: c })),
    [],
  );

  const buildDraftUpdateBody = (): UpdatePurchaseOrderDTO | null => {
    if (!d) return null;
    for (let i = 0; i < draftLines.length; i++) {
      const line = draftLines[i];
      if (
        !line ||
        !z.string().uuid().safeParse(line.shopProductSkuId).success
      ) {
        toast.error(`ເລືອກ SKU ບັນທັດທີ່ ${i + 1}`);
        return null;
      }
      if (
        line.qtyOrdered === "" ||
        !Number.isInteger(line.qtyOrdered) ||
        line.qtyOrdered < 1
      ) {
        toast.error(`ຈຳນວນສັ່ງບັນທັດທີ່ ${i + 1} ຕ້ອງ ≥ 1`);
        return null;
      }
    }
    const body: UpdatePurchaseOrderDTO = {
      orderedAt: d.orderedAt ?? null,
      expectedAt: d.expectedAt ?? null,
      note: note.trim() || null,
      currency: currency as (typeof SHOP_CURRENCIES)[number],
      supplierId: supplierId,
      supplierName: supplierId ? undefined : supplierName.trim() || null,
      supplierRef: supplierId ? undefined : supplierRef.trim() || null,
      lines: draftLines.map((l) => ({
        shopProductSkuId: l.shopProductSkuId,
        qtyOrdered: Number(l.qtyOrdered),
        unitCost: l.unitCost,
        lineNote: l.lineNote,
      })),
    };
    return body;
  };

  /** GRN: ແຕ່ລະແຖວ = ບັນທັດຮັບ 1 ຊັ້ນ — ສຳລັບຕາຕະລາງດຽວ */
  const grnFlatRows = useMemo(() => {
    const receipts = receiptsQuery.data?.receipts;
    if (!receipts?.length) return [];
    const out: {
      key: string;
      receivedAt: string;
      receiptId: string;
      note: string | null;
      productName: string;
      shopSku: string;
      qtyReceived: number;
      locationCode: string | null;
    }[] = [];
    for (const r of receipts) {
      for (const line of r.lines) {
        out.push({
          key: `${r.id}-${line.id}`,
          receivedAt: r.receivedAt,
          receiptId: r.id,
          note: r.note,
          productName: line.productName,
          shopSku: line.shopSku,
          qtyReceived: line.qtyReceived,
          locationCode: line.locationCode ?? null,
        });
      }
    }
    return out;
  }, [receiptsQuery.data?.receipts]);

  const onSaveDraft = async () => {
    if (!shopId || !purchaseOrderId || !d) return;
    const body = buildDraftUpdateBody();
    if (!body) return;
    try {
      await updatePo.mutateAsync({
        purchaseOrderId,
        body,
      });
      toast.success("ບັນທຶກຮ່າງແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດບັນທຶກໄດ້");
    }
  };

  const onSubmitPo = async () => {
    if (!shopId || !purchaseOrderId) return;
    const body = buildDraftUpdateBody();
    if (!body) return;
    try {
      await updatePo.mutateAsync({ purchaseOrderId, body });
      await submitPo.mutateAsync(purchaseOrderId);
      toast.success("ສົ່ງໃບສັ່ງຊື້ແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ສົ່ງບໍ່ສຳເລັດ");
    }
  };

  const onCancelPo = async () => {
    if (!shopId || !purchaseOrderId) return;
    try {
      await cancelPo.mutateAsync(purchaseOrderId);
      toast.success("ຍົກເລີກແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ຍົກເລີກບໍ່ສຳເລັດ");
    }
  };

  if (!shopId || !purchaseOrderId) return null;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.purchaseOrderDetail(shopId)} />
      </Header>
      <Main>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="flex flex-wrap items-center gap-2 font-heading font-semibold text-xl tracking-tight">
              <ShoppingCart
                className="size-6 shrink-0 text-primary"
                aria-hidden
              />
              {detail.isLoading ? (
                <span className="text-muted-foreground">ກຳລັງໂຫຼດ…</span>
              ) : d ? (
                d.poNumber
              ) : (
                <span className="text-muted-foreground">ບໍ່ພົບໃບສັ່ງຊື້</span>
              )}
            </h1>
            {d ? (
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-muted-foreground text-sm">
                <PurchaseOrderStatusBadge status={d.status} />
                <span aria-hidden className="text-border">
                  ·
                </span>
                <span>
                  ສະກຸນ{" "}
                  <span className="font-mono text-card-foreground">
                    {d.currency}
                  </span>
                </span>
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={() =>
                nav({
                  to: LIST_ROUTE,
                  params: { shopId },
                  search: {
                    offset: 0,
                    limit: 20,
                    status: undefined,
                    branchId: undefined,
                    supplierId: undefined,
                    due: undefined,
                    sortBy: undefined,
                    sortOrder: undefined,
                  },
                })
              }
            >
              <ArrowLeftIcon className="size-4" aria-hidden />
              ກັບລາຍການ
            </Button>
          </div>
        </div>

        {detail.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : !d ? (
          <p className="text-muted-foreground">ບໍ່ພົບໃບສັ່ງຊື້</p>
        ) : (
          <div className="mx-auto max-w-6xl space-y-6">
            <div className="grid gap-3 rounded-lg border border-border bg-card p-4 text-sm sm:grid-cols-2">
              <div>
                <p className="text-muted-foreground text-xs">ສາຂາ</p>
                <p className="font-medium">
                  {d.branchName?.trim() || d.branchId.slice(0, 8)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">ສ້າງເມື່ອ</p>
                <p>{formatDateTimeLocal(d.createdAt)}</p>
              </div>
              {d.orderedAt ? (
                <div>
                  <p className="text-muted-foreground text-xs">ວັນທີສັ່ງ</p>
                  <p>{formatDateTimeLocal(d.orderedAt)}</p>
                </div>
              ) : null}
              {d.expectedAt ? (
                <div>
                  <p className="text-muted-foreground text-xs">ຄາດວ່າຈະຮອດ</p>
                  <p>{formatDateTimeLocal(d.expectedAt)}</p>
                </div>
              ) : null}
              {d.supplierName?.trim() || d.supplierRef?.trim() ? (
                <div className="sm:col-span-2">
                  <p className="text-muted-foreground text-xs">ຜູ້ສະໜອງ</p>
                  <p className="font-medium">
                    {d.supplierName?.trim() || "—"}
                    {d.supplierRef?.trim() ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · {d.supplierRef.trim()}
                      </span>
                    ) : null}
                  </p>
                </div>
              ) : null}
            </div>

            {canReceive &&
            firstReceivableSkuId &&
            (d.status === "submitted" || d.status === "partially_received") ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() =>
                    nav({
                      to: PO_RECEIVE_ROUTE,
                      params: { shopId, purchaseOrderId: d.id },
                      search: {
                        shopProductSkuId: firstReceivableSkuId,
                      },
                    })
                  }
                >
                  ຮັບຂອງຈາກ PO
                </Button>
              </div>
            ) : null}

            {isDraft && canAdjust ? (
              <div className="space-y-4 rounded-lg border border-border bg-muted/15 p-4">
                <p className="font-medium text-sm">ແກ້ໄຂຮ່າງ</p>
                <div className="space-y-2">
                  <Label htmlFor="currency">ສະກຸນເງິນ</Label>
                  <SimpleSelect
                    name="currency"
                    value={currency}
                    onValueChange={setCurrency}
                    options={currencyOptions}
                  />
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    ຖ້າປ່ຽນສະກຸນແລ້ວ SKU ບໍ່ກົງ ລະບົບຈະບໍ່ບັນທຶກ — ແກ້ທີ່ຄຳຂໍ SKU ຫຼືສ້າງ PO ສະກຸນອື່ນ
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>ຜູ້ສະໜອງ (ຈາກລາຍການ)</Label>
                  <SimpleSelect
                    value={supplierId ?? "__none__"}
                    onValueChange={(v) => {
                      if (v === "__none__") {
                        setSupplierId(null);
                        return;
                      }
                      setSupplierId(v);
                      const row = suppliersQuery.data?.data.find(
                        (x) => x.id === v,
                      );
                      if (row) {
                        setSupplierName(row.name);
                        setSupplierRef(row.refCode ?? "");
                      }
                    }}
                    options={[
                      { value: "__none__", label: "ພິມເອງ / ບໍ່ມີ" },
                      ...(suppliersQuery.data?.data.map((s) => ({
                        value: s.id,
                        label: s.name,
                      })) ?? []),
                    ]}
                    className="h-9 w-full"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="supplierName">ຊື່ຜູ້ສະໜອງ</Label>
                    <Input
                      id="supplierName"
                      value={supplierName}
                      onChange={(e) => setSupplierName(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplierRef">ເອກະສານອ້າງອີງ</Label>
                    <Input
                      id="supplierRef"
                      value={supplierRef}
                      onChange={(e) => setSupplierRef(e.target.value)}
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="note">ໝາຍເຫດ</Label>
                  <textarea
                    id="note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <ShopPurchaseOrderDraftLinesEditor
                  shopId={shopId}
                  currency={currency}
                  lines={draftLines}
                  onChange={setDraftLines}
                  disabled={updatePo.isPending || submitPo.isPending}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={updatePo.isPending}
                  onClick={() => void onSaveDraft()}
                >
                  {updatePo.isPending ? <Loader /> : "ບັນທຶກຮ່າງ"}
                </Button>
              </div>
            ) : null}

            {!(isDraft && canAdjust) ? (
              <div>
                <h2 className="mb-2 font-medium text-sm">ລາຍການສິນຄ້າ</h2>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <thead className="border-border border-b bg-muted/40">
                      <tr>
                        <th className="px-3 py-2">ສິນຄ້າ</th>
                        <th className="px-3 py-2">SKU</th>
                        <th className="px-3 py-2 text-right">ສັ່ງ / ຮັບແລ້ວ</th>
                        <th className="px-3 py-2 text-right">ຕົ້ນທຶນ</th>
                        <th className="px-3 py-2 text-right">ລວມ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.lines.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-3 py-6 text-center text-muted-foreground"
                          >
                            ບໍ່ມີບັນທັດ
                          </td>
                        </tr>
                      ) : (
                        d.lines.map((line) => (
                          <tr key={line.id} className="border-border border-b">
                            <td className="px-3 py-2">{line.productName}</td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {line.shopSku}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {line.qtyOrdered} / {line.qtyReceived}
                              {line.qtyOrdered > line.qtyReceived ? (
                                <span className="text-muted-foreground text-xs">
                                  {" "}
                                  (ຄ້າງ {line.qtyOrdered - line.qtyReceived})
                                </span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {line.unitCost != null
                                ? formatShopMoney(line.unitCost, d.currency)
                                : "—"}
                            </td>
                            <td className="px-3 py-2 text-right font-medium tabular-nums">
                              {line.unitCost != null
                                ? formatShopMoney(line.unitCost, d.currency)
                                : "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {d.lines.length > 0 ? (
                      <tfoot className="border-border border-t bg-muted/40">
                        <tr>
                          <td
                            colSpan={4}
                            className="px-3 py-2 text-right font-medium"
                          >
                            ຍອດລວມທັງໝົດ
                          </td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums">
                            {formatShopMoney(
                              d.lines.reduce(
                                (sum, l) => sum + (l.unitCost ?? 0),
                                0,
                              ),
                              d.currency,
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </div>
            ) : null}

            {d.status !== "draft" ? (
              <div className="space-y-3">
                <h2 className="font-medium text-sm">ປະຫວັດການຮັບສິນຄ້າ (GRN)</h2>
                {receiptsQuery.isLoading ? (
                  <div className="flex justify-center py-6">
                    <Loader />
                  </div>
                ) : receiptsQuery.isError ? (
                  <p className="text-muted-foreground text-sm">
                    ໂຫຼດປະຫວັດບໍ່ສຳເລັດ
                  </p>
                ) : grnFlatRows.length === 0 ? (
                  <p className="text-muted-foreground text-sm">ບໍ່ມີປະຫວັດຮັບ</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[720px] text-left text-sm">
                      <thead className="border-border border-b bg-muted/40">
                        <tr>
                          <th className="whitespace-nowrap px-3 py-2">
                            ວັນທີເວລາຮັບ
                          </th>
                          <th className="px-3 py-2">ສິນຄ້າ</th>
                          <th className="px-3 py-2">SKU</th>
                          <th className="px-3 py-2 text-right">ຈຳນວນຮັບ</th>
                          <th className="px-3 py-2">ຕຳແໜ່ງ</th>
                          <th className="px-3 py-2">ໝາຍເຫດ</th>
                          <th className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                            GRN
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {grnFlatRows.map((row) => (
                          <tr key={row.key} className="border-border border-b">
                            <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                              {formatDateTimeLocal(row.receivedAt)}
                            </td>
                            <td className="px-3 py-2">{row.productName}</td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {row.shopSku}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {row.qtyReceived}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {row.locationCode?.trim()
                                ? row.locationCode
                                : "—"}
                            </td>
                            <td className="max-w-[200px] px-3 py-2 text-muted-foreground text-xs">
                              {row.note?.trim() ? row.note : "—"}
                            </td>
                            <td className="px-3 py-2">
                              <code
                                className="text-[11px] text-muted-foreground"
                                title={row.receiptId}
                              >
                                {row.receiptId.slice(0, 8)}…
                              </code>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}

            {isDraft && canAdjust ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={submitPo.isPending}
                  onClick={() => void onSubmitPo()}
                >
                  {submitPo.isPending ? <Loader /> : "ສົ່ງ PO"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={cancelPo.isPending}
                  onClick={() => void onCancelPo()}
                >
                  {cancelPo.isPending ? <Loader /> : "ຍົກເລີກ"}
                </Button>
              </div>
            ) : null}

            {d.status === "submitted" && canAdjust ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={cancelPo.isPending}
                  onClick={() => void onCancelPo()}
                >
                  {cancelPo.isPending ? <Loader /> : "ຍົກເລີກ PO"}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </Main>
    </RequireMerchantPermission>
  );
}
